import { IGroupChatData } from "@home-health-notify/domain";
import { List } from "@mui/material";
import { useTranslation } from "hhn-commons";
import EmptyRecordView from "../../../common/EmptyRecordView";
import GoToTop from "../../../common/GoToTop";
import ScrollListContainer from "../../../common/ScrollListContainer";
import { useScrollAnchor } from "../../../hooks/useScrollAnchor";
import ProgressLoader from "../../../loader/ProgressLoader";
import { CHAT_DETAILS } from "./PatientChats";
import PatientGroupChatListItem from "./PatientGroupChatListItem";

interface ChatListProps {
  chats: IGroupChatData[];
  selectedChatId?: string;
  isChatLoading: boolean;
  isMoreChats: boolean;
  fetchChatList: () => Promise<void>;
  showChatDetails: CHAT_DETAILS;
}

function PatientGroupChatList({
  chats,
  selectedChatId,
  isChatLoading,
  fetchChatList,
  showChatDetails,
  isMoreChats,
}: ChatListProps): React.JSX.Element {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "chat" });

  const { scrollContainerRef, isShowGoToTopButton, goToTop } = useScrollAnchor({
    items: chats,
    getItemId: chat => chat.groupInfo.id,
    getItemVersion: chat => chat.updatedAt,
    selectedItemId: selectedChatId,
  });

  return (
    <>
      <ScrollListContainer
        ref={scrollContainerRef}
        isLoading={isChatLoading}
        dataTestId="groupChatList"
        onScrollCallback={fetchChatList}
        restTop={isChatLoading ? 252 : 230}
      >
        <>
          {isShowGoToTopButton && <GoToTop onClick={goToTop} />}
          {chats.length > 0 && (
            <List data-testid="chatList" sx={{ paddingTop: "0px" }}>
              {chats.map((chatData: IGroupChatData) => (
                <PatientGroupChatListItem
                  chatData={chatData}
                  key={chatData.groupInfo.id}
                  isSelected={selectedChatId === chatData.groupInfo.id}
                  showChatDetails={showChatDetails}
                />
              ))}
            </List>
          )}
          {!isChatLoading && !isMoreChats && chats.length !== 0 && (
            <EmptyRecordView emptyMessage={t("endOfTheListMessage")} />
          )}
          {!isChatLoading && chats.length === 0 && <EmptyRecordView />}
          {isChatLoading && (
            <ProgressLoader
              label={tNs("chatLoadingMessage")}
              style={{
                height: "auto",
              }}
            />
          )}
        </>
      </ScrollListContainer>
    </>
  );
}

export default PatientGroupChatList;
